<template>
  <div class="gs-asset-type">
    <div class="gs-asset-split">
      <template v-for="(ad, index) in logoutPageAds">
        <section v-if="isLogoutPageAdTabActive(ad)" :key="ad.id" class="gs-asset-editor">
          <header class="gs-asset-editor__head">
            <div class="gs-asset-editor__head-fields">
              <gs-field :label="$t('frontend_resources_name')">
                <text-input
                  v-model="ad.name"
                  :validator="$v.localFunnelResource.logout_page_ads.$each[index].name"
                  :attribute="$t('frontend_groovesell_ad_name')"
                  :placeholder="$t('frontend_name')"
                />
              </gs-field>
            </div>
            <div class="gs-asset-editor__controls">
              <span class="gs-asset-editor__toggle">
                <span>Show to affiliates</span>
                <gd-toggle v-model="ad.enabled" :value="ad.enabled" />
              </span>
              <button
                type="button"
                class="gs-asset-editor__delete"
                data-action="delete-ad"
                title="Delete ad"
                aria-label="Delete ad"
                @click="openConfirmModal(index)"
              >
                <trash-icon class="w-4 h-4 fill-current" />
              </button>
            </div>
          </header>

          <div class="gs-asset-editor__section">
            <span class="gs-asset-editor__label">{{ $t('frontend_resources_ad') }}</span>
            <gs-rich-text-editor
              v-model="ad.body"
              :html-view.sync="isLogoutAdsPageHTMLView"
              :variables="variableMenu"
              :upload-url="uploadUrl"
              :placeholder="$t('frontend_body')"
            />
          </div>
        </section>
      </template>

      <!-- Only shown when there is something to pick: with an empty list the
           list's own empty state is the single message. -->
      <div v-if="hasLogoutPageAds && !selectedLogoutPageAd" class="gs-asset-empty">
        <p>Select an ad from the list to edit it.</p>
      </div>
      <asset-item-list
        title="Logout page ads"
        :items="logoutPageAds"
        :selected-id="selectedLogoutPageAdId"
        :add-label="$t('frontend_resources_add_new')"
        name-fallback="Untitled ad"
        :empty-copy="$t('frontend_resources_you_currently_dont_have_logout')"
        enabled-key="enabled"
        :error-ids="logoutPageAdsErrorIds"
        @select="selectLogoutPageAd"
        @add="createLogoutPageAd()"
        @reorder="reorderLogoutPageAds"
      />
    </div>

    <gs-alert-dialog
      v-model="confirmOpen"
      title="Delete this ad?"
      :copy="$t('frontend_non_revertable')"
      :confirm-label="$t('frontend_delete_confirm')"
      @confirm="deleteLogoutPageAd()"
    />
  </div>
</template>

<script>
import generateNumberMixin from '@/mixins/generateNumberMixin';
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import GsRichTextEditor from '@/components/editor/GsRichTextEditor.vue';
import { buildVariableMenu } from '@/components/editor/editorVariables';
import TrashIcon from '@/assets/images/icons/TrashIcon.svg';
import AssetItemList from './AssetItemList.vue';
import { toTabOrder, sortByTabOrder } from './tabOrder';

export default {
  mixins: [generateNumberMixin],
  components: {
    AssetItemList,
    GsRichTextEditor,
    TrashIcon,
  },
  props: {
    funnelResource: {
      type: Object,
      required: true,
    },
    landingPages: {
      type: Array,
      required: true,
    },
    logoutPageAdsTab: {
      type: String,
      default: '',
    },
    uploadUrl: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      localFunnelResource: this.funnelResource,
      localLogoutPageAdsTab: this.logoutPageAdsTab,
      isLogoutAdsPageHTMLView: false,
      logoutPageAdId: null,
      confirmOpen: false,
    };
  },
  computed: {
    hasLogoutPageAds() {
      return (
        this.localFunnelResource &&
        this.localFunnelResource.logout_page_ads &&
        this.localFunnelResource.logout_page_ads.length
      );
    },
    logoutPageAds() {
      return (this.localFunnelResource && this.localFunnelResource.logout_page_ads) || [];
    },
    selectedLogoutPageAd() {
      return this.logoutPageAds.filter((ad) => this.isLogoutPageAdTabActive(ad))[0] || null;
    },
    selectedLogoutPageAdId() {
      if (!this.localLogoutPageAdsTab) return null;
      return this.localLogoutPageAdsTab.replace('logout_page_ad_tab_', '');
    },
    variableMenu() {
      // The Insert menu wears the same two labels the plain-text Insert
      // dropdowns beside it do, so both editors name the tokens identically.
      return buildVariableMenu(this.landingPages, {
        labels: {
          affiliateName: this.$t('frontend_resources_add_affiliate_name'),
          affiliateLink: this.$t('frontend_resources_add_affiliate_link'),
        },
      });
    },
    logoutPageAdsErrorIds() {
      let logoutPageAdsErrorIds = [];

      for (const index in this.$v.localFunnelResource.logout_page_ads.$each.$iter) {
        if (this.$v.localFunnelResource.logout_page_ads.$each.$iter[index].$error) {
          logoutPageAdsErrorIds.push(
            this.$v.localFunnelResource.logout_page_ads.$each.$iter[index].$model.id
          );
        }
      }

      return logoutPageAdsErrorIds;
    },
  },
  mounted() {
    this.$root.$on('trigger-validation', () => {
      this.$v.$touch();
    });
  },
  methods: {
    updateFunnelResource() {
      this.$emit('updateFunnelResource', this.localFunnelResource);
    },
    selectLogoutPageAd(id) {
      this.handleLogoutPageAdsTabChange(`logout_page_ad_tab_${id}`);
    },
    // `AssetItemList` reports the new order as ids; the existing handler speaks
    // the tab shape gd-tabs used, so the ids are wrapped back into it.
    reorderLogoutPageAds(ids) {
      this.handleLogoutPageAdsTabOrderUpdate(toTabOrder('logout_page_ad', ids));
    },
    handleLogoutPageAdsTabChange(newTab) {
      this.localLogoutPageAdsTab = newTab || this.localLogoutPageAdsTab;
      this.$emit('handleLogoutPageAdsTabChange', this.localLogoutPageAdsTab);
    },
    handleLogoutPageAdsTabOrderUpdate(updatedTabs) {
      sortByTabOrder(this.localFunnelResource.logout_page_ads, 'logout_page_ad', updatedTabs);

      this.updateFunnelResource();
    },
    createLogoutPageAd() {
      const newAdId = this.generateNumber();
      const newAdCount = this.localFunnelResource.logout_page_ads.length + 1;

      this.localFunnelResource.logout_page_ads.push({
        id: newAdId,
        name: `${this.$t('frontend_groovesell_tools_affiliate_edit_ad')} ${newAdCount}`,
        body: '',
      });

      this.localLogoutPageAdsTab = `logout_page_ad_tab_${newAdId}`;

      this.updateFunnelResource();
      this.handleLogoutPageAdsTabChange();
    },
    openConfirmModal(logoutPageAdId) {
      this.logoutPageAdId = logoutPageAdId;
      this.confirmOpen = true;
    },
    async deleteLogoutPageAd() {
      this.localFunnelResource.logout_page_ads.splice(this.logoutPageAdId, 1);
      this.localLogoutPageAdsTab = '';

      if (this.localFunnelResource.logout_page_ads.length > 0) {
        let nextOpenIndex = 0;
        if (this.logoutPageAdId != 0) {
          nextOpenIndex = this.logoutPageAdId - 1;
        }

        const nextOpenId =
          this.localFunnelResource.logout_page_ads[nextOpenIndex].id;
        this.localLogoutPageAdsTab = `logout_page_ad_tab_${nextOpenId}`;
      }

      this.updateFunnelResource();
      this.handleLogoutPageAdsTabChange();
    },
    isLogoutPageAdTabActive(ad) {
      return this.localLogoutPageAdsTab === `logout_page_ad_tab_${ad.id}`;
    },
  },
  validations: {
    localFunnelResource: {
      logout_page_ads: {
        $each: {
          name: {
            required,
            minLength: minLength(3),
            maxLength: maxLength(80),
          },
          $trackBy: 'id',
        },
      },
    },
  },
};
</script>

<style src="./asset-split.css"></style>
